import React, { useEffect, useState } from 'react';
import IconButton from 'lib/IconButton';
import startRecordingIcon from 'assets/icons/start-recording.svg';
import circleSpinnerIcon from 'assets/icons/circle-spinner.svg';
import stopRecordingIcon from 'assets/icons/stop-recording.svg';
import shareIcon from 'assets/icons/share.svg';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import cx from 'classnames';

export const TIME_FOR_DATADOG_TO_PROCESS_SESSION_IN_MS = 24 * 1000;

export const SessionRecorderButton: React.FC = () => {
  const [recordingStatus, setRecordingStatus] = useState<
    'startRecording' | 'recording' | 'processingRecording' | 'shareRecording'
  >('startRecording');

  const handleClick = async (): Promise<void> => {
    switch (recordingStatus) {
      case 'recording':
        try {
          datadogRum.stopSessionReplayRecording();
          setRecordingStatus('processingRecording');
        } catch (error) {
          datadogLogs.logger.error('Error occurred while stopping session recording.', {
            errorMessage: error.message,
            filePath: 'SessionRecorderButton',
          });
        }
        break;

      case 'processingRecording':
        break;

      case 'shareRecording':
        try {
          const sessionUrl = datadogRum.getSessionReplayLink();

          if (sessionUrl) {
            await navigator.clipboard.writeText(sessionUrl);
            // eslint-disable-next-line no-alert
            alert("The recording's link have been copied to your clipboard! ✅");
          } else {
            // eslint-disable-next-line no-alert
            alert("The recording's link is not available. The developers have been notified about this issue.");
          }
        } catch (error) {
          datadogLogs.logger.error("Error occurred while getting recorded session's URL.", {
            errorMessage: error.message,
            filePath: 'SessionRecorderButton',
          });
        }
        break;

      default:
        try {
          datadogRum.startSessionReplayRecording({ force: true });
          setRecordingStatus('recording');
        } catch (error) {
          datadogLogs.logger.error('Error occurred while starting session recording.', {
            errorMessage: error.message,
            filePath: 'SessionRecorderButton',
          });
        }
        break;
    }
  };

  const getButtonProps = (): { icon: string; name: string; analyticsId: string } => {
    switch (recordingStatus) {
      case 'recording':
        return {
          icon: stopRecordingIcon,
          name: 'Stop recording this tab',
          analyticsId: 'Stopped recording',
        };

      case 'processingRecording':
        return {
          icon: circleSpinnerIcon,
          name: 'Generating recording link...',
          analyticsId: 'Processing recording',
        };

      case 'shareRecording':
        return {
          icon: shareIcon,
          name: "Copy recording's link to clipboard",
          analyticsId: 'Shared recording',
        };

      default:
        return {
          icon: startRecordingIcon,
          name: 'Start recording this tab',
          analyticsId: 'Started recording',
        };
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (recordingStatus === 'processingRecording') {
      // DataDog takes time to process the recording
      timeoutId = setTimeout(() => {
        setRecordingStatus('shareRecording');
      }, TIME_FOR_DATADOG_TO_PROCESS_SESSION_IN_MS);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [recordingStatus]);

  const { icon, name, analyticsId } = getButtonProps();

  return (
    <IconButton
      icon={icon}
      name={name}
      className={cx('z-[9999]', {
        '[&>svg]:animate-spin': recordingStatus === 'processingRecording',
      })}
      theme={Theme.PRIMARY}
      buttonSize={ButtonSize.LARGE}
      handleOnClick={handleClick}
      isDisabled={recordingStatus === 'processingRecording'}
      ddActionName={`SessionRecorderButton | ${analyticsId}`}
    />
  );
};
